import {
  AccountCircleIconData,
  DarkModeIconData,
  LightModeIconData,
  LogoutIconData,
} from "@meiden-connect/material-symbols";
import { useMeidenConnectUser } from "@meiden-connect/auth-frontend-sdk";
import { BinarySwitch, Button, Icon, useThemeMode, useThemePreference } from "@meiden-connect/ui";
import { useMemo } from "react";
import { getWebConfig } from "../../env";
import { useCurrentActor } from "../../auth/use-current-actor";
import { saveDashboardThemePreference } from "../../theme/theme-preference";

const MEIDEN_CONNECT_CATALOG_URL = "https://catalog.ours.connect.meidensha.co.jp/";

export function DashboardHeader(): JSX.Element {
  const themeMode = useThemeMode();
  const [, setThemePreference] = useThemePreference();
  const currentThemeLabel = themeMode === "dark" ? "ダークモード" : "ライトモード";

  const handleChangeTheme = (value: "ライトモード" | "ダークモード"): void => {
    const next = value === "ダークモード" ? "dark" : "light";
    setThemePreference(next);
    saveDashboardThemePreference(next);
  };

  return (
    <header className="dashboard-header">
      <a className="dashboard-logo" href="/">
        <img alt="" className="dashboard-logo-mark" src="/app-icon.png" />
        <span>リファインメントキャンバス</span>
      </a>
      <div className="dashboard-header-actions">
        <MeidenConnectLogo isLoggedIn={getWebConfig().authMode === "required"} themeMode={themeMode} />
        <BinarySwitch
          aria-label="表示テーマ"
          className="theme-toggle-switch"
          onChange={handleChangeTheme}
          options={[
            { d: LightModeIconData, value: "ライトモード" },
            { d: DarkModeIconData, value: "ダークモード" },
          ]}
          title={currentThemeLabel}
          value={currentThemeLabel}
        />
        {getWebConfig().authMode === "required" ? <RequiredUserChip /> : <MockUserChip />}
      </div>
    </header>
  );
}

function MeidenConnectLogo(props: {
  readonly isLoggedIn: boolean;
  readonly themeMode: "dark" | "light";
}): JSX.Element {
  const logoSrc =
    props.themeMode === "dark" ? "/meiden-connect-logo-dark.png" : "/meiden-connect-logo-light.png";

  return (
    <a
      aria-label="MEIDEN CONNECT ソリューションカタログ"
      className={`meiden-connect-logo ${props.isLoggedIn ? "" : "meiden-connect-logo-disabled"}`}
      href={props.isLoggedIn ? MEIDEN_CONNECT_CATALOG_URL : undefined}
      rel={props.isLoggedIn ? "noopener noreferrer" : undefined}
      target={props.isLoggedIn ? "_blank" : undefined}
      title={props.isLoggedIn ? "ソリューションカタログを開く" : "ログイン中のみ利用できます"}
    >
      <span className="meiden-connect-logo-powered">Powered by</span>
      <img alt="MEIDEN CONNECT" className="meiden-connect-logo-img" src={logoSrc} />
    </a>
  );
}

function RequiredUserChip(): JSX.Element {
  const user = useMeidenConnectUser();
  const authServiceOrigin = getWebConfig().authServiceOrigin;
  const initials = useMemo(() => getInitials(user.name), [user.name]);
  const profileUrl = authServiceOrigin ? `${authServiceOrigin}/users/${user.userId}/profile` : undefined;

  return (
    <div className="dashboard-user-chip" aria-label={`ログイン中: ${user.name}`}>
      <a
        className="dashboard-user-identity-link"
        href={profileUrl}
        rel="noopener noreferrer"
        target="_blank"
        title="ユーザー情報を開く"
      >
        {user.avatarImageUrl ? (
          <img alt="ログインユーザーのアイコン" className="dashboard-user-avatar" src={user.avatarImageUrl} />
        ) : (
          <AvatarFallback initials={initials} />
        )}
        <span className="dashboard-user-meta">
          <strong>{user.name}</strong>
          <span>{user.email}</span>
        </span>
      </a>
      <Button
        aria-label="ログアウトする"
        className="dashboard-header-icon-button"
        onClick={() => void user.signOut()}
        title="ログアウトする"
        type="button"
        variant={undefined}
      >
        <Icon d={LogoutIconData} />
      </Button>
    </div>
  );
}

function MockUserChip(): JSX.Element {
  const actor = useCurrentActor();
  const initials = useMemo(() => getInitials(actor.displayName), [actor.displayName]);
  return (
    <div className="dashboard-user-chip" aria-label={`ローカル確認ユーザー: ${actor.displayName}`}>
      {actor.avatarImageUrl ? (
        <img alt="ローカル確認ユーザーのアイコン" className="dashboard-user-avatar" src={actor.avatarImageUrl} />
      ) : (
        <AvatarFallback initials={initials} />
      )}
      <span className="dashboard-user-meta">
        <strong>{actor.displayName}</strong>
        <span>ローカル確認</span>
      </span>
    </div>
  );
}

function AvatarFallback({ initials }: { readonly initials: string }): JSX.Element {
  return (
    <span aria-hidden="true" className="dashboard-user-avatar dashboard-user-avatar-fallback">
      {initials.length > 0 ? initials : <Icon d={AccountCircleIconData} />}
    </span>
  );
}

function getInitials(name: string): string {
  return name
    .split(/\s+/u)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}
